import { useApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { CustomRockGrading } from '@/stores/type/customRockGrading.type'
import { computed, ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'

const { api } = useApi()

const baseUrl = API.CORE.CUSTOM_ROCK_GRADINGS.INDEX

export const useCustomRockGradingStore = defineStore('customRockGrading', () => {
  const authorizationStore = useAuthorizationStore()

  const canAddCustomRockGrading = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_customrockgrading']
    })
  })

  const canChangeCustomRockGrading = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_customrockgrading']
    })
  })

  const canDeleteCustomRockGrading = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_customrockgrading']
    })
  })

  const baseStore = useBaseStore<CustomRockGrading>({ service: baseUrl, api })

  const filterFields = ref(['name'])

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  const createNewCustomRockGrading = (project: number) => {
    const newCustomRockGrading = {} as CustomRockGrading

    baseStore.fieldsList.value.forEach(({ parameter, children, default: defaultValue }) => {
      if (parameter === 'project') {
        newCustomRockGrading[parameter] = project
      } else if (children) {
        newCustomRockGrading[parameter] = Object.fromEntries(
          Object.entries(children).map(([subField, subFieldData]) => [
            subField,
            subFieldData.default
          ])
        )
      } else {
        newCustomRockGrading[parameter] = defaultValue
      }
    })

    return newCustomRockGrading
  }

  const updateSelectedItems = (checkBoxValue: boolean, filteredItems: CustomRockGrading[]) => {
    if (checkBoxValue) {
      filteredItems.forEach((item) => {
        if (!baseStore.selectedItems.value.includes(item)) {
          baseStore.selectedItems.value.push(item)
        }
      })
    } else {
      filteredItems.forEach((item) => {
        const index = baseStore.selectedItems.value.findIndex(
          (selectedItem) => selectedItem.id === item.id
        )
        if (index !== -1) {
          baseStore.selectedItems.value.splice(index, 1)
        }
      })
    }
  }

  const moveSelectedItemsToArchive = () => {
    console.log('move-selected-items-to-archive')
  }

  const duplicateSelectedItems = () => {
    console.log('duplicate-selected-items')
  }

  const deleteSelectedItems = () => {
    baseStore.openDeleteDialog()
  }

  const clearSelectedItems = () => {
    if (baseStore.items.value) {
      baseStore.selectedItems.value = baseStore.selectedItems.value.filter(
        (item: CustomRockGrading) => baseStore.items.value.includes(item)
      )
    } else {
      baseStore.selectedItems.value = []
    }
  }

  function resetAll() {
    baseStore.reset()
  }

  return {
    ...baseStore,
    canAddCustomRockGrading,
    canChangeCustomRockGrading,
    canDeleteCustomRockGrading,
    fieldsFiltered,
    createNewCustomRockGrading,
    updateSelectedItems,
    moveSelectedItemsToArchive,
    duplicateSelectedItems,
    deleteSelectedItems,
    clearSelectedItems,
    resetAll
  }
})
